import {
  BarChart3,
  Building2,
  Calendar,
  ChartNoAxesColumn,
  CircleDollarSign,
  ClipboardList,
  Contact,
  FileSpreadsheet,
  FileText,
  Folder,
  Handshake,
  Inbox,
  KanbanSquare,
  LayoutDashboard,
  LayoutGrid,
  Mail,
  Megaphone,
  MessageSquare,
  Package,
  Phone,
  Plug,
  Receipt,
  Route,
  Settings,
  Star,
  Tags,
  Users,
  Wallet,
  Workflow,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * Central, data-driven navigation model for the Gravun shell.
 *
 * The sidebar renders exclusively from this configuration, never duplicate
 * navigation JSX per route. Groups (except the top-level overview) render as
 * collapsible parents with chevrons, matching the Gravun design reference.
 *
 * `implemented: false` routes render the shared ComingSoon page so unbuilt
 * modules are an honest, controlled state instead of fake functionality.
 * `resource` (optional) names the ra-core resource whose "list" access gates
 * visibility, the hook for RBAC-aware menus as permissions expand.
 */
export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  implemented: boolean;
  resource?: string;
  /** Milestone tag shown on the ComingSoon page. */
  milestone?: string;
  /**
   * Plan entitlement feature key gating visibility (see
   * public.get_workspace_entitlements). Omitted = always visible.
   * UI gating only, the server enforces entitlements independently.
   */
  feature?: string;
}

export interface NavGroup {
  key: string;
  /** Groups without a label render items at the top level (no collapse). */
  label?: string;
  icon?: LucideIcon;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    key: "overview",
    items: [
      {
        key: "dashboard",
        label: "Dashboard",
        to: "/",
        icon: LayoutDashboard,
        implemented: true,
      },
    ],
  },
  {
    key: "crm",
    label: "CRM",
    icon: Users,
    items: [
      {
        key: "contacts",
        label: "Contacts",
        to: "/contacts",
        icon: Contact,
        implemented: true,
        resource: "contacts",
      },
      {
        key: "companies",
        label: "Companies",
        to: "/companies",
        icon: Building2,
        implemented: true,
        resource: "companies",
      },
      {
        key: "leads",
        label: "Leads",
        to: "/leads",
        icon: Zap,
        implemented: false,
        milestone: "Milestone 2",
      },
      {
        key: "deals",
        label: "Deals",
        to: "/deals",
        icon: Handshake,
        implemented: true,
        resource: "deals",
      },
      {
        key: "pipelines",
        label: "Pipelines",
        to: "/pipelines",
        icon: KanbanSquare,
        implemented: false,
        milestone: "Milestone 2",
      },
    ],
  },
  {
    key: "sales",
    label: "Sales",
    icon: CircleDollarSign,
    items: [
      {
        key: "estimates",
        label: "Estimates",
        to: "/estimates",
        icon: FileSpreadsheet,
        implemented: true,
        feature: "estimates",
      },
      {
        key: "invoices",
        label: "Invoices",
        to: "/invoices",
        icon: Receipt,
        implemented: true,
        feature: "invoicing",
      },
      {
        key: "payments",
        label: "Payments",
        to: "/payments",
        icon: Wallet,
        implemented: true,
        feature: "payments",
      },
      {
        key: "expenses",
        label: "Expenses",
        to: "/expenses",
        icon: Receipt,
        implemented: true,
      },
      {
        key: "pricebook",
        label: "Pricebook",
        to: "/products",
        icon: Package,
        implemented: true,
        feature: "pricebook",
      },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    icon: Wrench,
    items: [
      {
        key: "jobs",
        label: "Jobs",
        to: "/jobs",
        icon: ClipboardList,
        implemented: true,
        feature: "jobs",
      },
      {
        key: "calendar",
        label: "Calendar",
        to: "/calendar",
        icon: Calendar,
        implemented: true,
        feature: "calendar_booking",
      },
      {
        key: "tasks",
        label: "Tasks",
        to: "/tasks",
        icon: ClipboardList,
        implemented: true,
      },
      {
        key: "routes",
        label: "Routes",
        to: "/routes",
        icon: Route,
        implemented: false,
        milestone: "Milestone 3",
      },
      {
        key: "documents",
        label: "Documents",
        to: "/documents",
        icon: Folder,
        implemented: false,
        milestone: "Milestone 4",
      },
    ],
  },
  {
    key: "communications",
    label: "Communications",
    icon: MessageSquare,
    items: [
      {
        key: "inbox",
        label: "Inbox",
        to: "/inbox",
        icon: Inbox,
        implemented: true,
        feature: "twoway_email",
      },
      {
        key: "calls",
        label: "Calls",
        to: "/calls",
        icon: Phone,
        implemented: false,
        milestone: "Milestone 3",
      },
      {
        key: "sms",
        label: "SMS",
        to: "/sms",
        icon: MessageSquare,
        implemented: false,
        milestone: "Milestone 3",
      },
      {
        key: "email",
        label: "Email",
        to: "/email",
        icon: Mail,
        implemented: false,
        milestone: "Milestone 3",
      },
    ],
  },
  {
    key: "automation",
    label: "Automation",
    icon: Workflow,
    items: [
      {
        key: "sequences",
        label: "Sequences",
        to: "/sequences",
        icon: Zap,
        implemented: true,
        feature: "followup_sequences",
      },
      {
        key: "workflows",
        label: "Workflows",
        to: "/workflows",
        icon: Workflow,
        implemented: true,
        feature: "workflow_automation",
      },
      {
        key: "forms",
        label: "Forms",
        to: "/forms",
        icon: FileText,
        implemented: true,
        feature: "forms_builder",
      },
      {
        key: "campaigns",
        label: "Campaigns",
        to: "/campaigns",
        icon: Megaphone,
        implemented: true,
        feature: "campaigns",
      },
      {
        key: "reviews",
        label: "Reviews",
        to: "/reviews",
        icon: Star,
        implemented: true,
        feature: "review_automation",
      },
    ],
  },
  {
    key: "platform",
    label: "Platform",
    icon: LayoutGrid,
    items: [
      {
        key: "apps",
        label: "Apps",
        to: "/apps",
        icon: LayoutGrid,
        implemented: false,
        milestone: "Milestone 5",
      },
      {
        key: "integrations",
        label: "Integrations",
        to: "/integrations",
        icon: Plug,
        implemented: false,
        milestone: "Milestone 3",
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    icon: ChartNoAxesColumn,
    items: [
      {
        key: "reports",
        label: "Reports",
        to: "/reports",
        icon: BarChart3,
        implemented: true,
      },
      {
        key: "revenue",
        label: "Revenue",
        to: "/revenue",
        icon: CircleDollarSign,
        implemented: false,
        milestone: "Milestone 4",
      },
    ],
  },
  {
    key: "admin",
    label: "Admin",
    icon: Settings,
    items: [
      {
        key: "connections",
        label: "Connections",
        to: "/connections",
        icon: Plug,
        implemented: true,
      },
      {
        key: "settings",
        label: "Settings",
        to: "/settings",
        icon: Settings,
        implemented: true,
        resource: "configuration",
      },
      {
        key: "team",
        label: "Team",
        to: "/team",
        icon: Users,
        implemented: true,
      },
      {
        key: "tags",
        label: "Tags",
        to: "/tags-admin",
        icon: Tags,
        implemented: false,
        milestone: "Milestone 2",
      },
    ],
  },
];

/** Every not-yet-implemented item (each needs a ComingSoon route). */
export const UNIMPLEMENTED_NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap(
  (group) => group.items,
).filter((item) => !item.implemented);

/** Find the nav item matching a location pathname (for page titles). */
export const findNavItemByPath = (pathname: string): NavItem | undefined => {
  const all = NAV_GROUPS.flatMap((group) => group.items);
  if (pathname === "/") return all.find((item) => item.to === "/");
  return all
    .filter((item) => item.to !== "/")
    .find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
};

/**
 * Alpha sidebar: only implemented modules are visible so the product feels
 * complete instead of surrounded by placeholders. Unimplemented routes stay
 * reachable by URL (ComingSoon) and rejoin the sidebar as they ship.
 */
export const VISIBLE_NAV_GROUPS: NavGroup[] = NAV_GROUPS.map((group) => ({
  ...group,
  items: group.items.filter((item) => item.implemented),
})).filter((group) => group.items.length > 0);
